import type { PublicTopicSummary } from './apiTopicContracts';
import type { Root } from './prototypeTypes';
import type { TopicChapterListItem } from './apiContracts';
import type { TopicChapter, TopicRecord } from './topicStore';

/**
 * Short-lived, root-scoped Topic data from a rendered Home card.
 *
 * The complete navigation projection is intentionally read per Topic. Do not
 * seed it with paged Home results: a compact card must never become a complete
 * navigation record. This preview store only bridges first detail paint for
 * the exact card selected while Topic reads load.
 */
const previewsByRoot = new WeakMap<object, Map<string, TopicRecord>>();
// Home cards project an authoritative compact chapter total independently of
// their paged chapter arrays. Keep it separate from TopicRecord.engagement:
// the latter drives a two-value legacy metric and must not manufacture an
// audio duration when the card did not provide one.
const chapterCountsByRoot = new WeakMap<object, Map<string, number>>();
const chapterPreviewsByRoot = new WeakMap<object, Map<string, Map<number, TopicNavigationChapterPreview>>>();

export type TopicNavigationChapterPreview = {
	index: number;
	title: string;
	subtitle?: string;
	duration?: string;
};

export function rememberTopicNavigationPreviews(root: Root, topics: readonly PublicTopicSummary[]): void {
	let previews = previewsByRoot.get(root);
	if (!previews) {
		previews = new Map<string, TopicRecord>();
		previewsByRoot.set(root, previews);
	}
	let chapterCounts = chapterCountsByRoot.get(root);
	if (!chapterCounts) {
		chapterCounts = new Map<string, number>();
		chapterCountsByRoot.set(root, chapterCounts);
	}
	for (const topic of topics) {
		const preview = toTopicPreview(topic);
		if (!preview) continue;
		previews.set(preview.slug, preview);
		const chapterCount = knownPositiveInteger(topic.chapterCount ?? topic.engagement?.chapters);
		if (chapterCount !== undefined) chapterCounts.set(preview.slug, chapterCount);
	}
}

export function getTopicNavigationPreview(root: Root, slug: string): TopicRecord | undefined {
	return previewsByRoot.get(root)?.get(slug);
}

/** Remembers only the list fields already shown in the Topic chapter rail. */
export function rememberTopicNavigationChapters(root: Root, slug: string, chapters: readonly TopicChapterListItem[]): void {
	const topicSlug = slug.trim();
	if (!topicSlug) return;
	const byIndex = new Map<number, TopicNavigationChapterPreview>();
	for (const chapter of chapters) {
		if (!Number.isInteger(chapter.index) || chapter.index < 1 || byIndex.has(chapter.index)) continue;
		const title = chapter.title?.trim();
		if (!title) continue;
		const subtitle = chapter.subtitle?.trim();
		const duration = chapter.duration?.trim();
		byIndex.set(chapter.index, { index: chapter.index, title, ...(subtitle ? { subtitle } : {}), ...(duration ? { duration } : {}) });
	}
	let topics = chapterPreviewsByRoot.get(root);
	if (!topics) {
		topics = new Map<string, Map<number, TopicNavigationChapterPreview>>();
		chapterPreviewsByRoot.set(root, topics);
	}
	topics.set(topicSlug, byIndex);
}

export function getTopicNavigationChapterPreview(root: Root, slug: string, index: number): TopicNavigationChapterPreview | undefined {
	return chapterPreviewsByRoot.get(root)?.get(slug)?.get(index);
}

export function getTopicNavigationChapterCount(root: Root, slug: string): number | undefined {
	const explicitCount = chapterCountsByRoot.get(root)?.get(slug);
	if (explicitCount !== undefined) return explicitCount;
	const count = chapterPreviewsByRoot.get(root)?.get(slug)?.size;
	return count && count > 0 ? count : undefined;
}

export function clearTopicNavigationPreviews(root: Root): void {
	previewsByRoot.delete(root);
	chapterCountsByRoot.delete(root);
	chapterPreviewsByRoot.delete(root);
}

function toTopicPreview(topic: PublicTopicSummary): TopicRecord | null {
	const slug = topic.slug?.trim();
	const title = topic.title?.trim();
	if (!slug || !title) return null;
	const previewChapters = topic.previewChapters?.map((chapter): TopicChapter => ({
		id: chapter.id,
		index: chapter.index,
		title: chapter.title,
		duration: chapter.duration,
		previewBody: chapter.previewBody,
		hasAudio: chapter.hasAudio,
		hasVideo: chapter.hasVideo,
	})) ?? [];
	const lockedChapters = topic.lockedChapters?.map((chapter): TopicChapter => ({
		id: chapter.id,
		index: chapter.index,
		title: chapter.title,
		duration: chapter.duration,
		hasAudio: chapter.hasAudio,
		hasVideo: chapter.hasVideo,
	})) ?? [];
	// A card can have its compact counts projected separately from engagement.
	// Use only those explicit DTO values: the paged chapter arrays may be absent
	// or intentionally incomplete, so their length is never a fallback metric.
	const chapterCount = knownNumber(topic.chapterCount ?? topic.engagement?.chapters);
	const knownAudioMinutes = knownNumber(topic.audioMinutes ?? topic.minutes ?? topic.engagement?.mediaMinutes);
	const audioMinutes = knownAudioMinutes ?? 0;
	// paintTopicMetrics renders both values together. Supplying one missing value
	// would make its legacy fallback print a false zero, so expose metrics only
	// when this card actually supplied both values.
	const engagement = chapterCount !== undefined && knownAudioMinutes !== undefined
		? {
			chapters: chapterCount,
			mediaMinutes: knownAudioMinutes,
			opened: knownNumber(topic.engagement?.opened),
			saved: knownNumber(topic.engagement?.saved),
		}
		: undefined;
	return {
		id: topic.id,
		slug,
		title,
		subtitle: topic.subtitle ?? '',
		category: topic.category ?? '',
		source: topic.source,
		classification: topic.classification ?? null,
		coverBadge: topic.coverBadge,
		coverImageUrl: topic.coverImageUrl,
		coverIcon: topic.coverIcon ?? '',
		coverGradient: topic.coverGradient ?? 'grad-ai',
		previewChapters,
		lockedChapters,
		isPrivate: topic.source === 'private_ai' || topic.isPublic === 0,
		audioMinutes,
		isNewThisWeek: topic.isNewThisWeek === true,
		isRecommended: topic.isRecommended === true,
		subscribers: knownNumber(topic.subscribers),
		openCount: knownNumber(topic.openCount),
		favoriteCount: knownNumber(topic.favoriteCount),
		dynamicScore: knownNumber(topic.dynamicScore),
		createdAt: topic.createdAt,
		engagement,
		keywords: topic.keywords,
	};
}

function knownNumber(value: number | undefined): number | undefined {
	return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function knownPositiveInteger(value: number | undefined): number | undefined {
	return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}
